import {AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, Validator} from '@angular/forms';
import {ConfigService} from '../../../../core/services';
import {makeProvider} from '../../utils/abstract-control-value-accessor';

@Component({
  selector: 'tj-money-input',
  templateUrl: './money-input.component.html',
  styleUrls: ['./money-input.component.scss'],
  providers: [
    makeProvider(MoneyInputComponent),
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MoneyInputComponent),
      multi: true,
    },
  ],
})
export class MoneyInputComponent implements ControlValueAccessor, Validator, AfterViewInit {
  @ViewChild('input') inputRef: ElementRef;

  @Input() disabled: boolean;

  @Output() onEnter = new EventEmitter();

  innerValue: any = '';

  private propagateChange;

  private onTouchedCallback;

  constructor(public config: ConfigService) {}

  ngAfterViewInit() {
    this.inputRef.nativeElement.value = this.innerValue;
  }

  onChange(e: Event, value: any) {
    // Only allow numeric input with up to 2 decimal places
    const numericValue = this.sanitizeNumericInput(value);
    
    if (numericValue !== value) {
      // Update the input field with sanitized value
      this.inputRef.nativeElement.value = numericValue;
      value = numericValue;
    }
    
    this.innerValue = value;
    // Convert to number for proper validation, or null if empty
    const numberValue = value === '' ? null : parseFloat(value);
    this.propagateChange(numberValue);
  }

  onInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    
    // Only allow numeric input with up to 2 decimal places
    const numericValue = this.sanitizeNumericInput(value);
    
    if (numericValue !== value) {
      // Update the input field with sanitized value
      target.value = numericValue;
      this.innerValue = numericValue;
      // Convert to number for proper validation, or null if empty
      const numberValue = numericValue === '' ? null : parseFloat(numericValue);
      this.propagateChange(numberValue);
    }
  }

  private sanitizeNumericInput(value: string): string {
    // Remove any non-numeric characters except decimal point
    let sanitized = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      sanitized = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    return sanitized;
  }

  selectText() {
    this.inputRef.nativeElement.select();
  }

  onBlur() {
    this.onTouchedCallback();
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  writeValue(value: any) {
    // Convert number to string for display, or empty string if null/undefined
    this.innerValue = value !== null && value !== undefined ? value.toString() : '';
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  validate(c: AbstractControl): {[key: string]: any} {
    return c.valid ? null : c.errors;
  }

  onEnterPress() {
    this.onEnter.emit();
  }
}
